import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { ChangeEvent, KeyboardEvent, useContext, useEffect, useRef, useState } from 'react';
import Section from '../../../components/Main/Section/Section';
import styled from 'styled-components';
import { Add, Edit } from '@mui/icons-material';
import SliderStarsAsLabels from '../../../components/Sliders/StarsAsLabels/SliderStarsAsLabels';
import Pagination from '../../../components/Pagination/Pagination';
import PlatformSelect from '../../../components/PlatformSelect/PlatformSelect';
import MonitoringLabelFilter from '../../../components/MonitoringLabelFilter/MonitoringLabelFilter';
import { useTranslation } from 'react-i18next';
import { IReviewEntry } from '../../../shared/interfaces/IReviewEntry';
import Filter from '../../../components/Filter/Filter';
import PlatformService from '../../../services/PlatformService';
import { IPlatform } from '../../../shared/interfaces/IPlatform';
import { useNavigate } from 'react-router-dom';
import ReviewMonitoringService from '../../../services/ReviewMonitoringService';
import { IMonitoring } from '../../../shared/interfaces/IMonitoring';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import ActionBar from '../../../components/ActionBar/ActionBar';
import RightContainer from '../../../components/Containers/RightContainer';
import SortContainer from '../../../components/Containers/SortContainer';
import ContentContainer from '../../../components/Containers/ContentContainer';
import LeftContainer from '../../../components/Containers/LeftContainer';
import { AuthContext } from '../../../providers/AuthProvider';
import MonitoringNoContent from '../../../assets/images/monitoring_no_content.png';
import http from '../../../shared/HttpCommon';
import { CanceledError } from 'axios';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { format, isValid } from 'date-fns';
import Chip from '../../../components/Chip/Chip';
import StringUtils from '../../../shared/StringUtils';
import ReviewNew from '../../../components/Review/ReviewNew';
import { IReviewNew } from '../../../shared/interfaces/IReviewNew';
import { IReview } from '../../../shared/interfaces/IReview';
import CommentingService, { FilterOptions } from '../../../services/CommentingService';
import GroupService from '../../../services/GroupService';
import { IGroup } from '../../../shared/interfaces/IGroup';

const AddEditContainer = styled.div`
  margin-left: auto;
`;

const ReviewContainer = styled.div``;

interface Paging {
  pagesTotal: number;
  totalItems: number;
}

const defaultFilter: FilterOptions = {
  textSearch: '',
  status: '',
  offset: 0,
  pageSize: 10,
  orderBy: ['date desc'],
  type: 'MONITORINGS',
  score: [1, 5],
  withComments: true,
  withoutComments: true,
  isUpdated: false,
  withGmb: false,
  withMonitoring: true,
};

const Reviews = () => {
  const { setCurrentView } = useContext(CurrentViewContext);
  const { hasRole, exceedsLimit } = useContext(AuthContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [applyFilter, setApplyFilter] = useState(false);
  const [multiActionChecked, setMultiActionChecked] = useState(false);
  const [checkedReview, setCheckedReview] = useState<string[]>([]);
  const [monitoring, setMonitoring] = useState<IMonitoring[]>([]);
  const [status, setStatus] = useState<string[]>([
    'Offen',
    'Gemeldet',
    'Gelöscht',
    'Bewerter ermitteln',
    'Löschung abgelehnt',
  ]);
  const startDateInputRef = useRef<Date | null>(null);
  const endDateInputRef = useRef<Date | null>(null);
  const [startDateError, setStartDateError] = useState<boolean>(false);
  const [endDateError, setEndDateError] = useState<boolean>(false);
  const [reviews, setReviews] = useState<IReviewEntry[]>([]);
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [filter, setFilter] = useState<FilterOptions>(defaultFilter);
  const [paging, setPaging] = useState<Paging>({
    pagesTotal: 0,
    totalItems: 0,
  });
  const [page, setPage] = useState<number>(1);
  const reviewerRoles = [
    {
      key: 'employee',
      name: t('employee'),
    },
    {
      key: 'applicant',
      name: t('applicant'),
    },
  ];
  const [localText, setLocalText] = useState<string>(filter.textSearch || '');
  const [filterByCompanyName, setFilterByCompanyName] = useState<IMonitoring | null>(null);
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [filterByGroup, setFilterByGroup] = useState<string>('');

  const updateFilter = (fn: React.SetStateAction<FilterOptions>) => {
    setFilter(fn);
  };

  const loadAllReviews = () => {
    setApplyFilter(true);
  };

  useEffect(() => {
    if (!applyFilter) return;

    const controller = new AbortController();
    const getReviews = async () => {
      try {
        setLoading(true);
        const data: any = await CommentingService.getAllReviews(controller, filter);
        if (!data) {
          setLoading(false);
          setApplyFilter(false);
          return;
        } else {
          setLoading(false);
          setApplyFilter(false);
          const reviews = data.reviews ?? [];
          setReviews(reviews);
          setPaging(data.paging);
        }
      } catch (error) {
        if (error instanceof CanceledError) {
        } else {
          setLoading(false);
          setApplyFilter(false);
          console.error(error);
        }
      }
    };
    getReviews().catch((error) => console.log(error));

    return () => {
      controller.abort();
    };
  }, [applyFilter]);

  useEffect(() => {
    setCurrentView(t('nav_reviews'));
    document.title = `${t('app_branding')} ${t('nav_reviews')}`;
  }, [setCurrentView, t]);

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async () => {
      const response = await PlatformService.getPlatforms(setLoading, controller.signal);
      if (Array.isArray(response)) {
        setPlatforms(response);
      }
    };

    const getMonitoring = async () => {
      const monitoringEntries: IMonitoring[] = await ReviewMonitoringService.getMonitoring(
        true,
        'reviews',
        setLoading,
        controller.signal
      );

      if (monitoringEntries) {
        setMonitoring(monitoringEntries);
        setPlatforms((prevState) =>
          prevState.filter((v) => monitoringEntries.find((m) => m.platformId === v.id))
        );
      }
    };

    const loadGroups = async () => {
      const groupsData = await GroupService.getGroups();
      setGroups(groupsData);
    };

    (async () => {
      await getPlatforms();
      await getMonitoring();
      await loadGroups();
      loadAllReviews();
    })();

    return () => {
      controller.abort();
    };
  }, []);

  // USE EFFECT END

  const handleReviewChecked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = event.target;
    setCheckedReview((prevState) =>
      checked ? [...prevState, id] : prevState.filter((item) => item !== id)
    );
  };
  const handleStartDateChange = () => {
    const value = startDateInputRef.current;
    if (value === null) {
      updateFilter((prevState) => {
        if (!prevState.startDate) {
          return prevState;
        } else {
          const newState = { ...prevState, offset: 0 };
          delete newState['startDate'];
          return newState;
        }
      });
    } else {
      if (!isValid(value)) {
        setStartDateError(true);
        return;
      }
      updateFilter((prevState) => {
        if (prevState.startDate === new Date(value).toISOString()) {
          return prevState;
        } else {
          return {
            ...prevState,
            offset: 0,
            startDate: new Date(value).toISOString(),
          };
        }
      });
    }
    setPage(1);
  };

  const handleEndDateChange = () => {
    const value = endDateInputRef.current;
    if (value === null) {
      updateFilter((prevState) => {
        if (!prevState.endDate) {
          return prevState;
        } else {
          const newState = { ...prevState, offset: 0 };
          delete newState['endDate'];
          return newState;
        }
      });
    } else {
      if (!isValid(value)) {
        setEndDateError(true);
        return;
      }
      updateFilter((prevState) => {
        if (prevState.endDate === new Date(value).toISOString()) {
          return prevState;
        } else {
          return {
            ...prevState,
            offset: 0,
            endDate: new Date(value).toISOString(),
          };
        }
      });
    }
    setPage(1);
  };

  function convertBlocks(blocks: IReview['blocks']): any {
    if (!blocks) {
      return [];
    }
    return blocks.map((block) => ({
      ...block,
      text: block.text ?? null,
      rating: typeof block.rating == 'number' ? block.rating : parseInt(block.rating),
      sub_ratings: [],
    }));
  }

  function convertReview(review: IReview): IReviewNew['review'] {
    // @ts-ignore
    // IReview is an intersection of multiple types, some attributes of which cannot coexist
    // The raw data, however, matches how it's used even if it doesn't match the type declaration
    return review as IReviewNew['review'];
  }

  function convertReviewEntries(reviewEntries: any[]): IReviewNew[] {
    return reviewEntries.map((entry) => ({
      ...entry,
      id: entry.id,
      review: convertReview(entry.review),
      status: status,
    }));
  }

  return (
    <Section>
      <>
        {platforms.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              borderRadius: `${theme.shape.borderRadius}px`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <img
              width={300}
              src={MonitoringNoContent}
              alt="Monitoring no records"
            />
            <Typography sx={{ mt: theme.spacing(4) }}>{t('no_records')}</Typography>
            {hasRole('can-add') && !exceedsLimit('limitmonitoring', 1) && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('guide')}
                sx={{ mt: theme.spacing(2) }}
              >
                {t('add_monitoring')}
              </Button>
            )}
          </Box>
        ) : (
          <>
            {loading ? (
              <LinearProgress />
            ) : (
              <ContentContainer>
                <LeftContainer>
                  <ActionBar>
                    <Checkbox
                      checked={multiActionChecked}
                      onChange={() => {}}
                      sx={{ display: 'none', mr: theme.spacing(1) }}
                    />
                    <AddEditContainer>
                      {hasRole('can-edit') && (
                        <Button
                          variant="outlined"
                          startIcon={<Edit />}
                          onClick={() => navigate('edit')}
                          sx={{ mr: theme.spacing(1) }}
                        >
                          {t('edit_monitoring')}
                        </Button>
                      )}
                      {hasRole('can-add') &&
                        !exceedsLimit('limitmonitoring', monitoring.length + 1) && (
                          <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => navigate('guide')}
                          >
                            {t('add_monitoring')}
                          </Button>
                        )}
                    </AddEditContainer>
                  </ActionBar>
                  <SortContainer sx={{ mt: 0 }}>
                    <Typography variant="body2">
                      {paging.totalItems > 1
                        ? `${paging.totalItems} ${t('reviews')}`
                        : `${paging.totalItems} ${t('review')}`}
                    </Typography>
                    <FormControl
                      size="small"
                      sx={{ minWidth: '284px', mt: theme.spacing(2), ml: theme.spacing(2) }}
                    >
                      <InputLabel id="sort-select-label">{t('sort')}</InputLabel>
                      <Select
                        labelId="sort-select-label"
                        value={filter.orderBy || []}
                        input={<OutlinedInput label={t('sort')} />}
                        onChange={async (event) => {
                          const {
                            target: { value },
                          } = event;
                          const values = typeof value === 'string' ? value.split(',') : value;

                          function removeConflictingSelection(
                            conflictFirst: any,
                            conflictSecond: any
                          ) {
                            const isSelectedA = values.includes(conflictFirst);
                            const isSelectedB = values.includes(conflictSecond);

                            if (isSelectedA && isSelectedB) {
                              const lastValue = values[values.length - 1];
                              if (lastValue === conflictFirst || lastValue === conflictSecond) {
                                values.pop();
                              }
                            }
                          }

                          removeConflictingSelection('score asc', 'score desc');
                          removeConflictingSelection('date asc', 'date desc');

                          const validCombinations = new Set([
                            'score asc',
                            'score desc',
                            'date asc',
                            'date desc',
                          ]);
                          const filteredValues = values.filter((value) =>
                            validCombinations.has(value)
                          );

                          if (filteredValues.length > 0) {
                            await updateFilter((prevState) => ({
                              ...prevState,
                              offset: 0,
                              orderBy: filteredValues,
                            }));
                          } else {
                            await updateFilter((prevState) => {
                              const newState = { ...prevState, offset: 0 };
                              delete newState['orderBy'];
                              return newState;
                            });
                          }
                          await setPage(1);
                          loadAllReviews();
                        }}
                        label={t('sort')}
                      >
                        <MenuItem value="score asc">{t('score_asc')}</MenuItem>
                        <MenuItem value="score desc">{t('score_desc')}</MenuItem>
                        <MenuItem value="date asc">{t('date_asc')}</MenuItem>
                        <MenuItem value="date desc">{t('date_desc')}</MenuItem>
                      </Select>
                    </FormControl>
                  </SortContainer>
                  <ReviewContainer>
                    <ReviewNew
                      reviews={convertReviewEntries(reviews)}
                      commentingModule={false}
                      deletionModule={true}
                      statusModule={true}
                      monitoring={monitoring}
                      location={[]}
                      hideHeader={false}
                      hideFooter={false}
                      status={status}
                    />
                  </ReviewContainer>
                  <Pagination
                    count={paging.pagesTotal}
                    page={page}
                    itemDescription={t('reviews')}
                    showItemsPerPage={String(filter.pageSize)}
                    onPageChange={async (event: ChangeEvent<unknown>, pageNumber: number) => {
                      await setPage(pageNumber);
                      await updateFilter((prevState) => ({
                        ...prevState,
                        offset: prevState.pageSize * pageNumber - prevState.pageSize,
                      }));
                      loadAllReviews();
                    }}
                    onItemsPerPageChange={async (event: SelectChangeEvent) => {
                      await updateFilter((prevState) => ({
                        ...prevState,
                        pageSize: parseInt(event.target.value),
                        offset: 0,
                      }));
                      await setPage(1);
                      await setPaging({
                        pagesTotal: 0,
                        totalItems: 0,
                      });
                      loadAllReviews();
                    }}
                  />
                </LeftContainer>
                <RightContainer>
                  <Filter
                    filters={
                      <>
                        {hasRole('can-use-status') ? (
                          <FormControl
                            size="small"
                            sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                          >
                            <InputLabel id="status-label-filter-label">
                              {t('deletion_status')}
                            </InputLabel>
                            <Select
                              labelId="status-label-filter-label"
                              value={filter.status}
                              onChange={(event: SelectChangeEvent) => {
                                if (event.target.value === '') {
                                  updateFilter((prevState) => {
                                    return { ...prevState, offset: 0, status: '' };
                                  });
                                } else {
                                  updateFilter((prevState) => ({
                                    ...prevState,
                                    offset: 0,
                                    status: event.target.value,
                                  }));
                                }
                                setPage(1);
                              }}
                              label={t('deletion_status')}
                            >
                              <MenuItem value="">
                                <em>-</em>
                              </MenuItem>
                              {status.map((statusName: string, index) => {
                                return (
                                  <MenuItem
                                    key={index}
                                    value={statusName}
                                  >
                                    {statusName}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        ) : null}
                        <MonitoringLabelFilter
                          disabled={loading}
                          value={filterByCompanyName}
                          options={monitoring}
                          onChange={(newValue) => {
                            setFilterByCompanyName(newValue);
                            if (!newValue) {
                              updateFilter((prevState) => {
                                const newState = { ...prevState, offset: 0 };
                                delete newState['company'];
                                delete newState['platform'];
                                return newState;
                              });
                            } else {
                              const platformName = PlatformService.getPlatformName(
                                newValue.platformId,
                                platforms
                              );
                              updateFilter((prevState) => ({
                                ...prevState,
                                offset: 0,
                                company: newValue.company,
                                platform: platformName,
                              }));
                            }
                            setPage(1);
                          }}
                        />
                        {groups.length > 0 && (
                          <FormControl
                            size="small"
                            sx={{ minWidth: '284px', mt: theme.spacing(1), mb: theme.spacing(1) }}
                          >
                            <InputLabel id="group-filter-label">{t('nav_groups')}</InputLabel>
                            <Select
                              labelId="group-filter-label"
                              value={filterByGroup}
                              onChange={(event: SelectChangeEvent) => {
                                setFilterByGroup(event.target.value);
                                if (!event.target.value) {
                                  updateFilter((prevState) => {
                                    const newState = { ...prevState, offset: 0 };
                                    delete newState['groupId'];
                                    return newState;
                                  });
                                } else {
                                  updateFilter((prevState) => ({
                                    ...prevState,
                                    offset: 0,
                                    groupId: event.target.value,
                                  }));
                                }
                                setPage(1);
                              }}
                              label={t('nav_groups')}
                            >
                              <MenuItem value="">
                                <em>-</em>
                              </MenuItem>
                              {groups.map((group) => (
                                <MenuItem
                                  key={group.id}
                                  value={group.id}
                                >
                                  {group.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        <TextField
                          label={t('search')}
                          variant="outlined"
                          value={localText}
                          size="small"
                          sx={{ mb: theme.spacing(2), mt: theme.spacing(1) }}
                          onChange={(event) => {
                            setLocalText(event.target.value);
                            updateFilter((prevState) => ({
                              ...prevState,
                              offset: 0,
                              textSearch: StringUtils.toFilterText(event.target.value),
                            }));
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ textTransform: 'uppercase', fontWeight: '700' }}
                        >
                          {t('score')}
                        </Typography>
                        <SliderStarsAsLabels
                          value={filter.score}
                          onChange={(event: Event, newValue: number | number[]) => {
                            updateFilter((prevState) => {
                              const newScore = Array.isArray(newValue)
                                ? newValue
                                : [newValue, newValue];
                              if (JSON.stringify(prevState.score) === JSON.stringify(newScore)) {
                                return prevState;
                              } else {
                                return {
                                  ...prevState,
                                  offset: 0,
                                  score: newScore,
                                };
                              }
                            });
                            setPage(1);
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            textTransform: 'uppercase',
                            fontWeight: '700',
                            mt: theme.spacing(2),
                          }}
                        >
                          {t('date')}
                        </Typography>
                        <DesktopDatePicker
                          disabled={loading}
                          disableFuture
                          label={t('date_from')}
                          format="dd.MM.yyyy"
                          value={filter.startDate ? new Date(filter.startDate) : null}
                          onChange={(newValue) => {
                            startDateInputRef.current = newValue;
                            setStartDateError(false);
                          }}
                          onClose={handleStartDateChange}
                          slotProps={{
                            textField: {
                              onBlur: handleStartDateChange,
                              onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
                                if (event.key === 'Enter') {
                                  handleStartDateChange();
                                }
                              },
                              size: 'small',
                              sx: { my: theme.spacing(1) },
                              error: startDateError,
                            },
                          }}
                        />
                        <DesktopDatePicker
                          disabled={loading}
                          disableFuture
                          label={t('date_to')}
                          format="dd.MM.yyyy"
                          value={filter.endDate ? new Date(filter.endDate) : null}
                          onChange={(newValue) => {
                            endDateInputRef.current = newValue;
                            setEndDateError(false);
                          }}
                          onClose={handleEndDateChange}
                          slotProps={{
                            textField: {
                              onBlur: handleEndDateChange,
                              onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
                                if (event.key === 'Enter') {
                                  handleEndDateChange();
                                }
                              },
                              size: 'small',
                              sx: { my: theme.spacing(1) },
                              error: endDateError,
                            },
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            textTransform: 'uppercase',
                            fontWeight: '700',
                            mt: theme.spacing(2),
                          }}
                        >
                          {t('platform')}
                        </Typography>
                        <PlatformSelect
                          disabled={loading}
                          value={filter.platform || ''}
                          onChange={(event: SelectChangeEvent) => {
                            const platformName = event.target.value || '';
                            if (platformName === '') {
                              updateFilter((prevState) => {
                                const newState = { ...prevState, offset: 0 };
                                delete newState['platform'];
                                delete newState['company'];
                                delete newState['kind'];
                                return newState;
                              });
                            } else {
                              updateFilter((prevState) => {
                                setFilterByCompanyName(null);
                                const newState = {
                                  ...prevState,
                                  offset: 0,
                                  platform: platformName,
                                };
                                delete newState['company'];
                                delete newState['kind'];
                                return newState;
                              });
                            }
                            setPage(1);
                          }}
                          platforms={platforms}
                        />
                        {filter.platform === 'kununu' && (
                          <FormControl
                            disabled={loading}
                            size="small"
                            sx={{ minWidth: '284px', my: theme.spacing(1) }}
                          >
                            <InputLabel id="kununu-reviewer-role-filter-label">
                              {t('reviewer_role')}
                            </InputLabel>
                            <Select
                              labelId="kununu-reviewer-role-filter-label"
                              value={filter.kind || ''}
                              onChange={(event: SelectChangeEvent) => {
                                if (event.target.value === '') {
                                  updateFilter((prevState) => {
                                    const newState = { ...prevState, offset: 0 };
                                    delete newState['kind'];
                                    return newState;
                                  });
                                } else {
                                  updateFilter((prevState) => ({
                                    ...prevState,
                                    kind: event.target.value,
                                  }));
                                }
                                setPage(1);
                              }}
                              label={t('reviewer_role')}
                            >
                              <MenuItem value="">
                                <em>-</em>
                              </MenuItem>
                              {reviewerRoles.map(({ key, name }) => {
                                return (
                                  <MenuItem
                                    key={key}
                                    value={key}
                                  >
                                    {name}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        )}
                        <FormGroup sx={{ my: theme.spacing(1) }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={filter.withComments}
                                onChange={() => {
                                  if (filter.withComments && !filter.withoutComments) {
                                    updateFilter((prevState) => ({
                                      ...prevState,
                                      offset: 0,
                                      withComments: true,
                                      withoutComments: true,
                                    }));
                                  } else {
                                    updateFilter((prevState) => ({
                                      ...prevState,
                                      offset: 0,
                                      withComments: !prevState.withComments,
                                    }));
                                  }
                                  setPage(1);
                                }}
                                inputProps={{ 'aria-label': 'controlled' }}
                              />
                            }
                            label={
                              <Typography variant="body2">{t('reviews_with_comment')}</Typography>
                            }
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={filter.withoutComments}
                                onChange={() => {
                                  if (!filter.withComments && filter.withoutComments) {
                                    updateFilter((prevState) => ({
                                      ...prevState,
                                      offset: 0,
                                      withComments: true,
                                      withoutComments: true,
                                    }));
                                  } else {
                                    updateFilter((prevState) => ({
                                      ...prevState,
                                      offset: 0,
                                      withoutComments: !prevState.withoutComments,
                                    }));
                                  }
                                  setPage(1);
                                }}
                                inputProps={{ 'aria-label': 'controlled' }}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {t('reviews_without_comment')}
                              </Typography>
                            }
                          />
                        </FormGroup>
                        {hasRole('can-use-deletion') && (
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={filter.hasDeletion}
                                  onChange={() => {
                                    updateFilter((prevState) => ({
                                      ...prevState,
                                      offset: 0,
                                      hasDeletion: !prevState.hasDeletion,
                                    }));
                                    setPage(1);
                                  }}
                                  inputProps={{ 'aria-label': 'controlled' }}
                                />
                              }
                              label={
                                <Typography variant="body2">{t('reviews_to_delete')}</Typography>
                              }
                            />
                          </FormGroup>
                        )}
                        {hasRole('can-use-note') && (
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={filter.hasNotes}
                                  onChange={() => {
                                    updateFilter((prevState) => ({
                                      ...prevState,
                                      offset: 0,
                                      hasNotes: !prevState.hasNotes,
                                    }));
                                    setPage(1);
                                  }}
                                  inputProps={{ 'aria-label': 'controlled' }}
                                />
                              }
                              label={
                                <Typography variant="body2">{t('reviews_with_notes')}</Typography>
                              }
                            />
                          </FormGroup>
                        )}
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={filter.isUpdated}
                                onChange={() => {
                                  updateFilter((prevState) => ({
                                    ...prevState,
                                    offset: 0,
                                    isUpdated: !prevState.isUpdated,
                                  }));
                                  setPage(1);
                                }}
                                inputProps={{ 'aria-label': 'controlled' }}
                              />
                            }
                            label={<Typography variant="body2">{t('updated_ratings')}</Typography>}
                          />
                        </FormGroup>
                        {hasRole('can-use-deleted-reviews') && (
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={filter.isDeleted}
                                  onChange={() => {
                                    updateFilter((prevState) => ({
                                      ...prevState,
                                      offset: 0,
                                      isDeleted: !prevState.isDeleted,
                                    }));
                                    setPage(1);
                                  }}
                                  inputProps={{ 'aria-label': 'controlled' }}
                                />
                              }
                              label={
                                <Typography variant="body2">{t('deleted_reveiws')}</Typography>
                              }
                            />
                          </FormGroup>
                        )}
                        <FormControl
                          size="small"
                          sx={{ minWidth: '284px', my: theme.spacing(1) }}
                        >
                          <InputLabel id="benchmarking-filter-select-label">
                            {t('record_type')}
                          </InputLabel>
                          <Select
                            labelId="benchmarking-filter-select-label"
                            value={filter.type}
                            onChange={(event: SelectChangeEvent) => {
                              if (event.target.value === '') {
                                updateFilter((prevState) => {
                                  const newState = { ...prevState, offset: 0 };
                                  newState.type = '';
                                  return newState;
                                });
                              } else {
                                updateFilter((prevState) => ({
                                  ...prevState,
                                  offset: 0,
                                  type: event.target.value,
                                }));
                              }
                              setPage(1);
                            }}
                            label={t('record_type')}
                          >
                            <MenuItem value="ALL">{t('all_records')}</MenuItem>
                            <MenuItem value="MONITORINGS">{t('own')}</MenuItem>
                            <MenuItem value="BENCHMARKS">{t('benchmarking')}</MenuItem>
                          </Select>
                        </FormControl>
                        <Divider sx={{ mt: theme.spacing(1) }} />
                        <Button
                          disabled={loading}
                          variant="contained"
                          onClick={async () => {
                            await setPage(1);
                            loadAllReviews();
                          }}
                          sx={{ mt: theme.spacing(1) }}
                        >
                          {t('apply')}
                        </Button>
                      </>
                    }
                    activeFilters={
                      <>
                        {filter.status && (
                          <Chip
                            label={`Status: ${filter.status}`}
                            onDelete={() => {
                              updateFilter((prevState) => {
                                const newState = { ...prevState, offset: 0 };
                                delete newState['status'];
                                return newState;
                              });
                              setPage(1);
                            }}
                          />
                        )}
                        {filter.company && (
                          <Chip
                            label={`Monitoring: ${
                              monitoring.find((m) => m.company === filter.company)?.label ||
                              filter.company
                            }`}
                            onDelete={() => {
                              updateFilter((prevState) => {
                                const newState = { ...prevState, offset: 0 };
                                delete newState['company'];
                                delete newState['platform'];
                                return newState;
                              });
                              setPage(1);
                            }}
                          />
                        )}
                        {filter.groupId && (
                          <Chip
                            label={`${t('nav_groups')}: ${
                              groups.find((g) => g.id === filter.groupId)?.name || filter.groupId
                            }`}
                            onDelete={() => {
                              updateFilter((prevState) => {
                                const newState = { ...prevState, offset: 0 };
                                delete newState['groupId'];
                                return newState;
                              });
                              setFilterByGroup('');
                              setPage(1);
                            }}
                          />
                        )}
                        {filter.textSearch && (
                          <Chip
                            label={`Suche: ${StringUtils.fromFilterText(filter.textSearch)}`}
                            onDelete={() => {
                              updateFilter((prevState) => {
                                const newState = { ...prevState, offset: 0 };
                                delete newState['textSearch'];
                                return newState;
                              });
                              setPage(1);
                            }}
                          />
                        )}
                        {(filter.score[0] !== 1 || filter.score[1] !== 5) && (
                          <Chip
                            label={`${t('stars')} ${filter.score[0]} - ${filter.score[1]}`}
                            onDelete={() => {
                              updateFilter((prevState) => ({
                                ...prevState,
                                offset: 0,
                                score: [1, 5],
                              }));
                              setPage(1);
                            }}
                          />
                        )}
                        {filter.startDate &&
                          isValid(new Date(filter.startDate)) &&
                          !filter.endDate && (
                            <Chip
                              label={`${t('date_from')}: ${format(
                                new Date(filter.startDate),
                                'dd.MM.yyyy'
                              )}`}
                              onDelete={() => {
                                updateFilter((prevState) => {
                                  const newState = { ...prevState, offset: 0 };
                                  delete newState['startDate'];
                                  return newState;
                                });
                                startDateInputRef.current = null;
                                setPage(1);
                              }}
                            />
                          )}
                        {!filter.startDate &&
                          filter.endDate &&
                          isValid(new Date(filter.endDate)) && (
                            <Chip
                              label={`${t('date_to')}: ${format(
                                new Date(filter.endDate),
                                'dd.MM.yyyy'
                              )}`}
                              onDelete={() => {
                                updateFilter((prevState) => {
                                  const newState = { ...prevState, offset: 0 };
                                  delete newState['endDate'];
                                  return newState;
                                });
                                endDateInputRef.current = null;
                                setPage(1);
                              }}
                            />
                          )}
                        {filter.startDate &&
                          isValid(new Date(filter.startDate)) &&
                          filter.endDate &&
                          isValid(new Date(filter.endDate)) && (
                            <Chip
                              label={`${format(
                                new Date(filter.startDate),
                                'dd.MM.yyyy'
                              )} - ${format(new Date(filter.endDate), 'dd.MM.yyyy')}`}
                              onDelete={() => {
                                updateFilter((prevState) => {
                                  const newState = { ...prevState, offset: 0 };
                                  delete newState['startDate'];
                                  delete newState['endDate'];
                                  return newState;
                                });
                                startDateInputRef.current = null;
                                endDateInputRef.current = null;
                                setPage(1);
                              }}
                            />
                          )}
                        {filter.platform && (
                          <Chip
                            label={platforms.find((p) => p.label === filter.platform)?.name || ''}
                            onDelete={() => {
                              updateFilter((prevState) => {
                                const newState = { ...prevState, offset: 0 };
                                delete newState['platform'];
                                delete newState['company'];
                                delete newState['kind'];
                                return newState;
                              });
                              setPage(1);
                            }}
                          />
                        )}
                        {filter.kind && (
                          <Chip
                            label={reviewerRoles.find((f) => f.key === filter.kind)?.name || ''}
                            onDelete={() => {
                              updateFilter((prevState) => {
                                const newState = { ...prevState, offset: 0 };
                                delete newState['kind'];
                                return newState;
                              });
                              setPage(1);
                            }}
                          />
                        )}
                        {filter.withComments && !filter.withoutComments && (
                          <Chip
                            label={t('reviews_with_comment')}
                            onDelete={() => {
                              updateFilter((prevState) => ({
                                ...prevState,
                                offset: 0,
                                withoutComments: true,
                              }));
                              setPage(1);
                            }}
                          />
                        )}
                        {filter.withoutComments && !filter.withComments && (
                          <Chip
                            label={t('reviews_without_comment')}
                            onDelete={() => {
                              updateFilter((prevState) => ({
                                ...prevState,
                                offset: 0,
                                withComments: true,
                              }));
                              setPage(1);
                            }}
                          />
                        )}
                        {filter.isUpdated && (
                          <Chip
                            label={'Aktualisierte Bewertungen'}
                            onDelete={() => {
                              updateFilter((prevState) => {
                                const newState = { ...prevState, offset: 0 };
                                delete newState['isUpdated'];
                                return newState;
                              });
                              setPage(1);
                            }}
                          />
                        )}
                        {filter.hasDeletion && (
                          <Chip
                            label={t('reviews_to_delete')}
                            onDelete={() => {
                              updateFilter((prevState) => {
                                const newState = { ...prevState, offset: 0 };
                                delete newState['hasDeletion'];
                                return newState;
                              });
                              setPage(1);
                            }}
                          />
                        )}
                        {filter.hasNotes && (
                          <Chip
                            label={t('reviews_with_notes')}
                            onDelete={() => {
                              updateFilter((prevState) => {
                                const newState = { ...prevState, offset: 0 };
                                delete newState['hasNotes'];
                                return newState;
                              });
                              setPage(1);
                            }}
                          />
                        )}
                        {filter.isDeleted && (
                          <Chip
                            label={t('deleted_reveiws')}
                            onDelete={() => {
                              updateFilter((prevState) => ({
                                ...prevState,
                                offset: 0,
                                isDeleted: true,
                              }));
                              setPage(1);
                            }}
                          />
                        )}
                        {filter.type !== 'ALL' && (
                          <Chip
                            label={`type: ${filter.type}`}
                            onDelete={() => {
                              updateFilter((prevState) => {
                                const newState = { ...prevState, offset: 0 };
                                newState.type = '';
                                return newState;
                              });
                              setPage(1);
                            }}
                          />
                        )}
                        {filter.withComments && filter.withoutComments && (
                          <Chip label={t('all_reviews')} />
                        )}
                      </>
                    }
                    resetActiveFilters={async () => {
                      await updateFilter(defaultFilter);
                      startDateInputRef.current = null;
                      endDateInputRef.current = null;
                      await setPage(1);
                      loadAllReviews();
                    }}
                  />
                </RightContainer>
              </ContentContainer>
            )}
          </>
        )}
      </>
    </Section>
  );
};

export default Reviews;
